import { Component, type ReactNode } from "react";
import { logger } from "@/lib/logger";
import { Sentry } from "@/lib/sentry";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const isChunkError =
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('is not a valid JavaScript MIME type') ||
      error.message?.includes('Importing a module script failed') ||
      error.message?.includes('Unable to preload CSS');

    if (isChunkError) {
      const reloadKey = '__lilia_chunk_reload__';
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1');
        window.location.reload();
        return;
      }
    }

    logger.error(
      "React render error",
      {
        message: error.message,
        stack: error.stack,
        componentStack: info.componentStack ?? undefined,
      },
      "react",
    );

    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? undefined } },
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center bg-[var(--lilia-bg,#fff)]">
          <div className="w-16 h-16 rounded-xl bg-red-100 flex items-center justify-center mb-6">
            <span className="text-3xl">!</span>
          </div>
          <h2 className="text-xl font-semibold text-[var(--lilia-text,#334155)] mb-2">Something went wrong</h2>
          <p className="text-sm text-[var(--lilia-text-muted,#94a3b8)] mb-1 max-w-md">
            The editor encountered an unexpected error.
          </p>
          {this.state.error && (
            <p className="text-xs text-red-500 font-mono mb-6 max-w-md truncate">
              {this.state.error.message}
            </p>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-[var(--lilia-primary,#0A76A4)] text-[color:var(--lilia-on-primary)] hover:opacity-90"
            >
              Refresh page
            </button>
            <button
              type="button"
              onClick={() => window.location.href = "/dashboard"}
              className="px-5 py-2.5 rounded-xl text-sm font-medium text-[var(--lilia-text-muted,#94a3b8)] hover:bg-[var(--lilia-surface,#f1f5f9)]"
            >
              Go to dashboard
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
